import {useCallback, useMemo, useRef, useState} from "react";

export type PreviewLanguageSession = Readonly<{
  isPreviewSessionActive: boolean;
  setPreviewSessionActive: (active: boolean) => void;
  sourceLanguageCode: string;
  translationLanguages: string[];
  activeTranslationLanguage: string | null;
  selectedLanguageCode: string;
  setProjectLanguages: (sourceLanguage: string, languages: string[]) => void;
  openTranslationLanguage: (languageCode: string) => void;
  closeTranslationLanguage: () => void;
  resetPreviewLanguageSession: () => void;
}>;

export const usePreviewLanguageSession = (): PreviewLanguageSession => {
  const [isPreviewSessionActive, setPreviewSessionActiveState] = useState(false);
  const [sourceLanguageCode, setSourceLanguageCode] = useState("");
  const [translationLanguages, setTranslationLanguages] = useState<string[]>([]);
  const [activeTranslationLanguage, setActiveTranslationLanguage] = useState<string | null>(null);
  const [selectedLanguageCode, setSelectedLanguageCode] = useState("");
  const previewLanguageInitializedRef = useRef(false);
  const isPreviewSessionActiveRef = useRef(false);
  const defaultPreviewLanguageRef = useRef("");

  const setPreviewSessionActive = useCallback((active: boolean) => {
    isPreviewSessionActiveRef.current = active;
    const defaultLanguage = defaultPreviewLanguageRef.current;
    if (active && defaultLanguage && !previewLanguageInitializedRef.current) {
      setActiveTranslationLanguage((previous) => previous ?? defaultLanguage);
      setSelectedLanguageCode((previous) => previous || defaultLanguage);
      previewLanguageInitializedRef.current = true;
    }
    setPreviewSessionActiveState(active);
  }, []);

  const setProjectLanguages = useCallback((sourceLanguage: string, languages: string[]) => {
    const defaultLanguage = languages[0] || sourceLanguage;
    defaultPreviewLanguageRef.current = defaultLanguage;
    setSourceLanguageCode((previous) => previous === sourceLanguage ? previous : sourceLanguage);
    setTranslationLanguages((previous) => {
      if (previous.length === languages.length && previous.every((code, index) => code === languages[index])) {
        return previous;
      }
      return languages;
    });
    setSelectedLanguageCode((previous) => previous || defaultLanguage);
    if (isPreviewSessionActiveRef.current && defaultLanguage && !previewLanguageInitializedRef.current) {
      setActiveTranslationLanguage((previous) => previous ?? defaultLanguage);
      previewLanguageInitializedRef.current = true;
    }
  }, []);

  const openTranslationLanguage = useCallback((languageCode: string) => {
    setActiveTranslationLanguage(languageCode);
    setSelectedLanguageCode(languageCode);
  }, []);

  const closeTranslationLanguage = useCallback(() => {
    setActiveTranslationLanguage(null);
  }, []);

  const resetPreviewLanguageSession = useCallback(() => {
    previewLanguageInitializedRef.current = false;
    defaultPreviewLanguageRef.current = "";
    setPreviewSessionActive(false);
    setSourceLanguageCode("");
    setTranslationLanguages([]);
    setActiveTranslationLanguage(null);
    setSelectedLanguageCode("");
  }, [setPreviewSessionActive]);

  return useMemo(() => ({
    isPreviewSessionActive,
    setPreviewSessionActive,
    sourceLanguageCode,
    translationLanguages,
    activeTranslationLanguage,
    selectedLanguageCode,
    setProjectLanguages,
    openTranslationLanguage,
    closeTranslationLanguage,
    resetPreviewLanguageSession,
  }), [isPreviewSessionActive, setPreviewSessionActive, sourceLanguageCode, translationLanguages, activeTranslationLanguage, selectedLanguageCode, setProjectLanguages, openTranslationLanguage, closeTranslationLanguage, resetPreviewLanguageSession]);
};
